import { alpha, Box, Button, Tooltip } from '@mui/material';
import { Trash2 } from 'lucide-react';
import { useIntl } from 'react-intl';
import { useConfirmClear } from '~/hooks/useConfirmClear';

interface AnnotationClearAllButtonProps {
    /** Length of the list being cleared; disarms the confirmation at zero. */
    itemCount: number;
    onClear: () => void;
    labelId: string;
    confirmLabelId: string;
    /** Says what would be cleared. */
    tooltip: string;
}

/**
 * Two-step "Clear all" for an annotation list: the first click arms, the second
 * clears (docs/ui_specs.md: a destructive clinical action needs a confirmation
 * step). Owns {@link useConfirmClear} rather than taking its result, so the
 * armed state and the button that shows it cannot be wired up apart.
 */
export default function AnnotationClearAllButton({
    itemCount,
    onClear,
    labelId,
    confirmLabelId,
    tooltip,
}: AnnotationClearAllButtonProps) {
    const intl = useIntl();
    const { confirming, requestClear } = useConfirmClear(itemCount, onClear);
    // Both labels share one cell and the hidden one keeps its width, so arming
    // does not resize the button or reflow what sits beside it.
    const label = (id: string, shown: boolean) => (
        <Box
            component="span"
            style={{
                gridArea: '1 / 1',
                visibility: shown ? undefined : 'hidden',
            }}
        >
            {intl.formatMessage({ id }, { count: itemCount })}
        </Box>
    );
    const button = (
        <Button
            size="small"
            variant={confirming ? 'contained' : 'text'}
            color={confirming ? 'error' : 'inherit'}
            startIcon={<Trash2 size={14} />}
            onClick={requestClear}
            // A held key activates it once: its repeats cannot confirm.
            onKeyDown={(event) => {
                if (event.repeat && ['Enter', ' '].includes(event.key)) {
                    event.preventDefault();
                }
            }}
            disabled={itemCount === 0}
            data-testid="annotation-clear-all"
            sx={({ palette }) => ({
                flexShrink: 0,
                whiteSpace: 'nowrap',
                px: 1,
                ...(!confirming && {
                    color: palette.text.secondary,
                    '&:hover': {
                        color: palette.error.light,
                        bgcolor: alpha(palette.error.main, 0.08),
                    },
                }),
            })}
        >
            <Box component="span" sx={{ display: 'grid' }}>
                {label(labelId, !confirming)}
                {label(confirmLabelId, confirming)}
            </Box>
        </Button>
    );
    // A description, so the button's name stays what a click will do.
    return (
        <Tooltip title={tooltip} placement="bottom" describeChild>
            {button}
        </Tooltip>
    );
}
